import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyEvent, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useT } from '@hana/i18n';
import {
  IconDots,
  IconGridDots,
  IconLayoutGrid,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLogout,
  IconMenu2,
  IconMessageReport,
  IconMoon,
  IconSearch,
  IconSun,
  IconX,
} from '@tabler/icons-react';
import { AppIcon, useTheme } from '@hana/ui';
import { AppsRow, AppsMenu } from './AppsMenu';
import { AskDock } from './AskDock';
import { ShellCtx, type ShellApi } from './context';
import { Feedback } from './Feedback';
import { activeKey, allItems, groupOf, initials, phoneTabs, sum, EXACT } from './nav';
import { NavIcon, NavTree } from './NavTree';
import { QuickFind } from './QuickFind';
import { useLayout } from './useLayout';
import type { Zone } from './zone';
import s from './shell.module.css';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export interface ShellUser {
  name: string;
  email: string;
  /** dòng phụ dưới tên (chức danh / ghế) */
  title?: string;
  /** ghế có cờ ADMIN: lưới ▦ thêm ô HANA Kit */
  admin?: boolean;
}

const NAV_KEY = 'hana.nav.rail';
const readRail = () => {
  try {
    return localStorage.getItem(NAV_KEY) === '1';
  } catch {
    return false;
  }
};

/** Khung app của một vùng theo MINT NAV KIT v1 + MINT APPS KIT v1 (board Mint-Home · -iPad · -Phone):
 *  sidebar 248 ⇄ rail 64 · nhóm module · ▦ chuyển vùng · menu tài khoản · dock Ask HANA (vỏ) · Góp ý (vỏ) · Tìm nhanh.
 *  Màn bên trong dùng <PageHeader> làm đầu trang. */
const noNav = (): undefined => undefined;

export function AppShell({ zone: zoneIn, user, onSignOut, children }: { zone: Zone; user: ShellUser; onSignOut: () => void; children: ReactNode }) {
  const viewNav = (zoneIn.useNav ?? noNav)();
  const zone = useMemo<Zone>(() => (viewNav ? { ...zoneIn, ...viewNav } : zoneIn), [zoneIn, viewNav]);
  const t = useT();
  const layout = useLayout();
  const { theme, toggle } = useTheme();
  const path = useRouterState({ select: (r) => r.location.pathname });
  const active = activeKey(zone, path);
  const screen = allItems(zone).find((i) => i.key === active)?.label ?? zone.name;

  const [railPref, setRailPref] = useState(readRail);
  const [floatNav, setFloatNav] = useState(false);
  const [fly, setFly] = useState<{ key: string; top: number } | null>(null);
  const [um, setUm] = useState(false);
  const [apps, setApps] = useState<'side' | 'rail' | 'float' | null>(null);
  const [more, setMore] = useState(false);
  const [fbMore, setFbMore] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  // bảng "Thêm" (điện thoại): đưa focus vào khi mở, giữ Tab trong bảng, trả focus về nút khi đóng
  useEffect(() => {
    if (!more) return;
    const from = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => {
      if (from?.isConnected) from.focus();
    };
  }, [more]);
  const trapTab = useCallback((e: ReactKeyEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const list = Array.from(e.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = list[0];
    const last = list[list.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) (e.preventDefault(), last.focus());
    else if (!e.shiftKey && document.activeElement === last) (e.preventDefault(), first.focus());
  }, []);
  const [find, setFind] = useState(false);
  const [fb, setFb] = useState(false);
  const [condensed, setCond] = useState(false);
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const [hideBars, setHideBars] = useState(false);
  const lastY = useRef(0);
  const flyT = useRef<ReturnType<typeof setTimeout>>(undefined);

  const rail = layout === 'rail' || (layout === 'side' && railPref);
  const closeAll = useCallback(() => {
    setFloatNav(false);
    setFly(null);
    setUm(false);
    setApps(null);
    setMore(false);
  }, []);
  useEffect(closeAll, [path, layout, closeAll]);

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setFind(true);
      }
      if (e.key === 'Escape') closeAll();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [closeAll]);

  const setRail = (v: boolean) => {
    setRailPref(v);
    try {
      localStorage.setItem(NAV_KEY, v ? '1' : '0');
    } catch {
      /* bỏ qua */
    }
  };

  const onScroll = (e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    const y = el.scrollTop;
    // Thu gọn đầu trang có trễ (vào > 48px, ra < 4px) và chỉ khi trang đủ dài: thu gọn làm đầu trang thấp đi,
    // trang ngắn bị kéo scrollTop về dưới ngưỡng → mở lại → dài ra → thu lại… (lỗi "nhảy" 29/Oct).
    const room = el.scrollHeight - el.clientHeight;
    setCond((c) => (room < 120 ? false : c ? y > 4 : y > 48));
    // điện thoại: cuộn xuống quá 40px ẩn thanh trên + dưới; cuộn lên hiện lại
    if (layout === 'phone') setHideBars(y > 40 && y > lastY.current);
    lastY.current = y;
  };

  const api = useMemo<ShellApi>(() => ({ layout, condensed, scrollRoot, openFind: () => setFind(true), openNav: () => setFloatNav(true), openFbMore: () => setFbMore(true), findButton: zone.findButton !== false || layout === 'pad' || layout === 'phone', admin: !!user.admin }), [layout, condensed, scrollRoot, zone.findButton, user.admin]);

  const themeK = theme === 'dark' ? t('theme.toLight') : t('theme.toDark');
  const ThemeIc = theme === 'dark' ? IconSun : IconMoon;
  const touch = layout === 'pad' || layout === 'phone';

  const userMenu = (
    <div role="menu" aria-label={t('shell.userMenu')} className={`${s.menu} ${s.um}`}>
      <button type="button" role="menuitem" className={s.mi} onClick={() => (setUm(false), setFb(true))}>
        <IconMessageReport size={16} />
        {t('shell.feedback')}
      </button>
      <button type="button" role="menuitem" className={s.mi} onClick={() => (setUm(false), toggle())}>
        <ThemeIc size={16} />
        {themeK}
      </button>
      <div className={s.hr} />
      <button type="button" role="menuitem" className={`${s.mi} ${s.miErr}`} onClick={onSignOut}>
        <IconLogout size={16} />
        {t('shell.signOut')}
      </button>
    </div>
  );

  const sidebar = (variant: 'fixed' | 'float' | 'pad') => (
    <nav
      aria-label={t('shell.nav')}
      className={`${s.side} ${variant !== 'fixed' ? s.float : ''} ${variant === 'pad' ? s.padNav : ''}`}
      onMouseLeave={variant === 'float' ? () => setFloatNav(false) : undefined}
    >
      <div className={s.sideHead}>
        <AppIcon app={zone.key} size={variant === 'pad' ? 32 : 30} />
        <div className={s.zoneName}>{zone.name}</div>
        <button
          type="button"
          className={s.ib}
          aria-label={t('shell.zones')}
          title={t('shell.zones')}
          aria-haspopup="menu"
          aria-expanded={apps !== null}
          onClick={() => setApps(apps ? null : variant === 'fixed' ? 'side' : 'float')}
        >
          <IconLayoutGrid size={18} stroke={2.2} />
        </button>
        {variant === 'fixed' ? (
          <button type="button" className={s.ib} aria-label={t('shell.collapse')} title={t('shell.collapse')} onClick={() => setRail(true)}>
            <IconLayoutSidebarLeftCollapse size={18} />
          </button>
        ) : (
          <button type="button" className={s.ib} aria-label={t('shell.closeMenu')} title={t('shell.closeMenu')} onClick={() => setFloatNav(false)}>
            {layout === 'side' ? <IconLayoutSidebarLeftExpand size={18} /> : <IconX size={18} />}
          </button>
        )}
      </div>
      <NavTree zone={zone} active={active} iconSize={variant === 'pad' ? 19 : 17} onGo={() => setFloatNav(false)} />
      <div className={s.me}>
        <span className={s.av} aria-hidden="true">
          {initials(user.name)}
        </span>
        <div className={s.meTx} title={user.email}>
          <b>{user.name || user.email}</b>
          <span>{user.title ?? user.email}</span>
        </div>
        <button type="button" className={`${s.ib} ${s.ibCtl}`} aria-label={themeK} title={themeK} onClick={toggle}>
          <ThemeIc size={16} />
        </button>
        <button type="button" className={`${s.ib} ${s.ibCtl}`} aria-label={t('shell.userMenu')} aria-haspopup="menu" aria-expanded={um} onClick={() => setUm(!um)}>
          <IconDots size={16} />
        </button>
        {um && (
          <>
            <div className={s.scrimClear} onClick={() => setUm(false)} />
            {userMenu}
          </>
        )}
      </div>
    </nav>
  );

  const flyGroup = fly ? zone.nav.find((g) => g.key === fly.key) : undefined;
  const actGroup = groupOf(zone, active)?.key;

  const railNav = (
    <nav aria-label={t('shell.nav')} className={s.rail}>
      <div className={s.railIn}>
        <button type="button" className={`${s.rb} ${s.rbCtl}`} aria-label={t('shell.openMenu')} title={t('shell.openMenu')} onClick={() => setFloatNav(true)}>
          <IconMenu2 size={18} />
        </button>
        <button
          type="button"
          className={s.rb}
          aria-label={t('shell.zones')}
          title={t('shell.zones')}
          aria-haspopup="menu"
          aria-expanded={apps === 'rail'}
          onClick={() => setApps(apps ? null : 'rail')}
        >
          <IconLayoutGrid size={18} stroke={2.2} />
        </button>
        {(zone.top ?? []).map((it) => (
          <Link activeOptions={EXACT} key={it.key} to={it.to} className={s.rb} aria-label={it.label} title={it.label} aria-current={it.key === active ? 'page' : undefined}>
            <NavIcon icon={it.icon} size={18} />
            {it.n ? <span className={s.rdot} aria-hidden="true" /> : null}
          </Link>
        ))}
        {zone.nav.map((g, i) => (
          <div key={g.key} style={{ display: 'contents' }}>
            {(i === 0 || g.section) && <div className={s.rsep} aria-hidden="true" />}
            <button
              type="button"
              className={`${s.rb} ${g.key === actGroup ? s.rbAct : ''}`}
              aria-label={g.label}
              title={g.label}
              aria-haspopup="menu"
              aria-expanded={fly?.key === g.key}
              onClick={(e) => setFly(fly?.key === g.key ? null : { key: g.key, top: e.currentTarget.offsetTop })}
              onPointerEnter={(e) => {
                if (e.pointerType !== 'mouse') return;
                const top = e.currentTarget.offsetTop;
                clearTimeout(flyT.current);
                flyT.current = setTimeout(() => setFly({ key: g.key, top }), 150);
              }}
              onPointerLeave={() => clearTimeout(flyT.current)}
            >
              <NavIcon icon={g.icon} size={18} />
              {sum(g) > 0 && <span className={s.rdot} aria-hidden="true" />}
            </button>
          </div>
        ))}
        <button type="button" className={`${s.rb} ${s.rbEnd}`} aria-label={themeK} title={themeK} onClick={toggle}>
          <ThemeIc size={18} />
        </button>
        {layout === 'side' && (
          <button type="button" className={s.rb} aria-label={t('shell.expand')} title={t('shell.expand')} onClick={() => setRail(false)}>
            <IconLayoutSidebarLeftExpand size={18} />
          </button>
        )}
      </div>
    </nav>
  );

  const labeled = zone.rail === 'labeled' && (layout === 'side' || layout === 'rail');
  const labeledRail = (
    <nav aria-label={t('shell.nav')} className={s.lrail}>
      <div className={s.lbrand}>
        <AppIcon app={zone.key} size={42} />
      </div>
      {[...(zone.top ?? []).map((it) => ({ key: it.key, label: it.short ?? it.label, aria: it.label, to: it.to, icon: it.icon, n: it.n ?? 0, on: it.key === active, sep: !!it.sep })),
        ...zone.nav.map((g) => ({ key: g.key, label: g.short ?? g.label, aria: g.label, to: g.items[0]?.to ?? '/', icon: g.icon, n: sum(g), on: g.key === actGroup, sep: !!g.section }))].map((it) => (
        <Fragment key={it.key}>
          {it.sep && <div className={s.lsep} aria-hidden="true" />}
          <Link to={it.to} className={s.lb} aria-label={it.aria} title={it.aria} aria-current={it.on ? 'page' : undefined}>
            <NavIcon icon={it.icon} size={20} />
            {it.label}
            {it.n > 0 && <span className={s.lbadge}>{it.n}</span>}
          </Link>
        </Fragment>
      ))}
      <button type="button" className={`${s.lb} ${s.lbEnd}`} aria-label={t('shell.zones')} title={t('shell.zones')} aria-haspopup="menu" aria-expanded={apps === 'rail'} onClick={() => setApps(apps ? null : 'rail')}>
        <IconGridDots size={20} stroke={2.6} />
        {t('shell.zone')}
      </button>
      <button type="button" className={s.lb} aria-label={themeK} title={themeK} onClick={toggle}>
        <ThemeIc size={20} />
        {theme === 'dark' ? t('shell.light') : t('shell.dark')}
      </button>
      <button type="button" className={s.lb} aria-label={t('shell.userMenu')} aria-haspopup="menu" aria-expanded={um} onClick={() => setUm(!um)}>
        <span className={s.av} aria-hidden="true">
          {initials(user.name)}
        </span>
        {t('shell.me')}
      </button>
      {um && (
        <>
          <div className={s.scrimClear} onClick={() => setUm(false)} />
          {userMenu}
        </>
      )}
    </nav>
  );

  const content = (
    <main ref={setScrollRoot} className={s.main} onScroll={onScroll}>
      <div className={s.content}>{children}</div>
    </main>
  );

  const overlays = (
    <>
      {apps && <AppsMenu current={zone.key} at={apps} admin={!!user.admin} onClose={() => setApps(null)} />}
      <AskDock context={`${zone.name} › ${screen}`} user={user.name} />
      <Feedback open={fb} onOpenChange={setFb} zone={zone.name} screen={screen} />
      <QuickFind open={find} onOpenChange={setFind} zone={zone} />
    </>
  );

  if (zone.bare?.some((b) => path === b || path.startsWith(`${b}/`))) {
    return (
      <ShellCtx.Provider value={api}>
        <div className={s.root}>
          <main ref={setScrollRoot} className={`${s.main} ${s.bare}`} onScroll={onScroll}>
            {children}
          </main>
          {overlays}
        </div>
      </ShellCtx.Provider>
    );
  }

  if (layout === 'phone') {
    const tabs = phoneTabs(zone);
    return (
      <ShellCtx.Provider value={api}>
        <div className={`${s.root} ${s.phone} ${s.touch}`}>
          <header className={`${s.pTop} ${condensed ? s.cond : ''} ${hideBars ? s.hide : ''}`}>
            <div className={s.pTitle}>
              <AppIcon app={zone.key} size={30} />
              <span>{zone.name}</span>
            </div>
            <button type="button" className={s.pb} aria-label={themeK} onClick={toggle}>
              <ThemeIc size={21} />
            </button>
            <button type="button" className={s.pb} aria-label={t('common.search')} onClick={() => setFind(true)}>
              <IconSearch size={21} stroke={2.2} />
            </button>
          </header>
          {content}
          <nav aria-label={t('shell.navMain')} className={`${s.tabs} ${hideBars ? s.hide : ''}`}>
            {tabs.map((t) => {
              const on = t.key === active || groupOf(zone, active)?.items[0]?.key === t.key;
              return (
                <Link activeOptions={EXACT} key={t.key} to={t.to} className={s.tab} aria-current={on ? 'page' : undefined}>
                  <NavIcon icon={t.icon} size={24} />
                  <span>{t.label}</span>
                </Link>
              );
            })}
            <button type="button" className={s.tab} aria-haspopup="dialog" aria-expanded={more} onClick={() => setMore(true)}>
              <IconDots size={24} />
              <span>{t('shell.moreTab')}</span>
            </button>
          </nav>
          {fbMore && (
            <>
              <div className={s.scrim} style={{ zIndex: 60 }} onClick={() => setFbMore(false)} />
              <div role="dialog" aria-modal="true" aria-label={t('shell.moreTab')} className={s.sheet} onKeyDown={(e) => e.key === 'Escape' && setFbMore(false)}>
                <div className={s.grab} aria-hidden="true" />
                <div className={s.sheetHead}>
                  <b>{t('shell.moreTab')}</b>
                  <button type="button" className={s.pb} aria-label={t('common.close')} autoFocus onClick={() => setFbMore(false)}>
                    <IconX size={20} />
                  </button>
                </div>
                <button type="button" className={s.mi} onClick={() => (setFbMore(false), setFb(true))}>
                  <IconMessageReport size={18} />
                  {t('shell.feedback')}
                </button>
                <div className={s.hr} />
                <button type="button" className={s.mi} onClick={() => (setFbMore(false), toggle())}>
                  <ThemeIc size={18} />
                  {themeK}
                </button>
              </div>
            </>
          )}
          {more && (
            <>
              <div className={s.scrim} style={{ zIndex: 60 }} onClick={() => setMore(false)} />
              <div ref={sheetRef} role="dialog" aria-modal="true" aria-label={t('shell.moreTab')} className={s.sheet} onKeyDown={trapTab}>
                <div className={s.grab} aria-hidden="true" />
                <div className={s.sheetHead}>
                  <b>{t('shell.moreTab')}</b>
                  <button type="button" className={s.pb} aria-label={t('common.close')} onClick={() => setMore(false)}>
                    <IconX size={20} />
                  </button>
                </div>
                <div className={s.navScroll}>
                  <AppsRow current={zone.key} admin={!!user.admin} />
                  <NavTree zone={zone} active={active} iconSize={20} onGo={() => setMore(false)} />
                  <div className={s.hr} />
                  <button type="button" className={s.mi} onClick={() => (setMore(false), setFb(true))}>
                    <IconMessageReport size={18} />
                    {t('shell.feedback')}
                  </button>
                  <button type="button" className={`${s.mi} ${s.miErr}`} onClick={onSignOut}>
                    <IconLogout size={18} />
                    {t('shell.signOut')}
                  </button>
                </div>
              </div>
            </>
          )}
          {overlays}
        </div>
      </ShellCtx.Provider>
    );
  }

  return (
    <ShellCtx.Provider value={api}>
      <div className={`${s.root} ${touch ? s.touch : ''}`}>
        {layout !== 'pad' && <div className={`${s.navCol} ${labeled ? s.isLabeled : rail ? s.isRail : ''}`}>{labeled ? labeledRail : rail ? railNav : sidebar('fixed')}</div>}
        {floatNav && (
          <>
            {layout === 'pad' ? <div className={s.scrim} onClick={() => setFloatNav(false)} /> : <div className={s.scrimClear} style={{ zIndex: 39 }} onClick={() => setFloatNav(false)} />}
            {sidebar(layout === 'pad' ? 'pad' : 'float')}
          </>
        )}
        {flyGroup && fly && (
          <>
            <div className={s.scrimClear} onClick={() => setFly(null)} />
            <div
              role="menu"
              aria-label={flyGroup.label}
              className={`${s.menu} ${s.fly}`}
              style={{ top: Math.max(8, fly.top - 6), maxHeight: `calc(100% - ${Math.max(8, fly.top - 6) + 8}px)` }}
              onMouseLeave={() => setFly(null)}
            >
              <div className={s.flyHead}>
                <b>{flyGroup.label}</b>
                <button type="button" className={s.ib} aria-label={t('common.close')} onClick={() => setFly(null)}>
                  <IconX size={15} />
                </button>
              </div>
              {flyGroup.items.map((it) => (
                <Link activeOptions={EXACT} key={it.key} to={it.to} role="menuitem" className={s.item} aria-current={it.key === active ? 'page' : undefined}>
                  <span>{it.label}</span>
                  {it.n ? <span className={s.cnt}>{it.n}</span> : null}
                </Link>
              ))}
            </div>
          </>
        )}
        {content}
        {overlays}
      </div>
    </ShellCtx.Provider>
  );
}
